import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';

import { useAuthStore } from '@/modules/auth/store';
import { authApi } from '@/utilities/axios';

import ReportService from '../services/ReportService';

import { incidentStore } from '../../../../../store/global-stores';
import { getFullName, removeHtmlTagsFromParagraph } from '@/utilities';
import { formatDate } from '@/utilities/date-formatter';
import { formatSingleSeriesBarChartData, formatMultiSeriesChartData } from '@/utilities/json-formatter';
import { getAllPriorities, getAllProgress } from '../../../constants';
import { getAllIncidentCategories } from '../../incident/constants';
import { showToast } from '@/utilities/toast-handler';

// Constants from env file
const REPORT_URL = process.env.VUE_APP_API_VERSION + '/report';
const TASK_REPORT_TEMPLATE_ID = process.env.VUE_APP_TASK_REPORT_TEMPLATE_ID;
const DEBRIEF_REPORT_TEMPLATE_ID = process.env.VUE_APP_DEBRIEF_REPORT_TEMPLATE_ID;
const CHUNK_SIZE = parseInt(process.env.VUE_APP_REPORT_CHUNKS_SIZE || 1000);

export const useReportStore = defineStore('report', () => {
    const authStore = useAuthStore();

    const { flatIncidentTypes } = storeToRefs(incidentStore);
    const incident_categories = getAllIncidentCategories();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });
    //states
    const params = ref({
        rows: 10,
        page: 0, //The request, apparently, starts with page+1 => set it to initial value 0.
    });
    const loading = ref(false);
    const totalTaskRecords = ref(null);
    const dynamicDebrief = ref(null);
    const dynamicTask = ref(null);
    const debriefCriteria = ref({
        company_id: null,
        incident_number: null,
        category: null,
        incident_type_id: [],
        incident_id: [],
        learning: 'Both',
        appliance_id: [],
        author_id: [],
        theme_id: [],
        learning_outcome_type: [],
        incident_date_from: null,
        incident_date_to: null,
    });
    const taskCriteria = ref({
        company_id: null,
        title: null,
        description: null,
        status: [],
        priority: [],
        start_date_from: null,
        start_date_to: null,
        due_date_from: null,
        due_date_to: null,
        source_id: [],
        incident_id: [],
        assigned_to_id: [],
        from_department_id: [],
        to_department_id: [],
        theme_id: [],
        overdue: null,
    });
    const progress_status = getAllProgress();
    const priority_type = getAllPriorities();

    const tasks = ref([]);

    //actions

    // ======> Debrief Reports Section:-
    // We use the same function for both graph and tabular requests on UI load
    // Output is of two types:-  'template_tabular' and 'template_graphs'
    const getDynamicDebriefReportData = async (output) => {
        // Pagination is only for the tabular request however sending it on the graphs wont impact anything
        const bodyparams = {
            ...debriefCriteria.value,
            company_id: company_id.value,
            learning: debriefCriteria.value.learning === 'Both' ? null : debriefCriteria.value.learning,
            page_size: params.value.rows,
            page: params.value.page + 1,
            output,
        };
        const response = await ReportService.fetchDebriefReport(bodyparams);
        if (response.statusCode === 200) {
            return response.data;
        }
        return null;
    };

    // API request to generate template response for downloading Debrief Report (For Print)
    const printDebriefReport = async (totalRecords) => {
        // While printing debrief report we need to take the following things into consideration
        // 1) We need two types of data:- Chart data and Tabular data
        // 2) Fetch Chart data first
        // To fetch chart data, edit the request payload output
        const chartData = await getDynamicDebriefReportData('template_graphs');
        // 3) Divide Tabular data request into multiple chunks (CHUNK_SIZE ) depending upon size of debriefs count which is initally loaded on table
        // Divide the totalRecords by chunk size so that we can figure out how many requests to be made
        const total_chunks = Math.ceil(totalRecords / CHUNK_SIZE);
        // Make a tabular data holder
        let rawTableData = [];
        // Now make a loop upto total_chunks
        for (let i = 0; i < total_chunks; i++) {
            // then make tabular requests each incrementing the current page with variable i
            // we will start with 0 because page is incremented by 1 on each request on getDynamicDebriefReportData function
            params.value.rows = CHUNK_SIZE;
            params.value.page = i;
            // Limiting it only upto 7k at the moment because APITemplate.io struggles to handle more than that
            if (i <= 6) {
                const tabularResponse = await getDynamicDebriefReportData('template_tabular');
                const chunkData = tabularResponse?.debriefs?.data;
                // the debrief data will be inside .debriefs.data so push each chunk into rawTableData
                rawTableData = rawTableData.concat(chunkData);
            }
        }
        // 3: Format each data :- chart information and tabular information and make a json
        const formattedChartData = formatDebriefCharts(chartData);
        const formattedTableData = formatDebriefTableData(rawTableData);

        let templateJson = {
            debriefCount: totalRecords,
            ...formattedChartData,
            tableData: formattedTableData,
        };
        // 4) Use that json and make request to the APITemplate server
        const reportType = 'debrief'; // its either 'task' or 'debrief'
        return downloadReport(templateJson, reportType);
    };

    const formatDebriefCharts = (chartData) => {
        // ======>Count of debriefs with Learning Identified by CallSign -> second param=> inner key, third param=> All Elements, fourth param => dataKey
        const debriefsLIByCallSignBar = formatMultiSeriesChartData(chartData.learning_by_appliances, 'learning', ['Yes', 'No'], 'tot');

        // ======> Outcome Type by CallSign
        const outcomeTypeByCallSignBar = formatMultiSeriesChartData(chartData.outcome_type_by_appliances, 'outcomes', ['Local Issue', 'Service Wide', 'Notable Practice'], 'tot');

        // ======> Outcome Type by Primary Themes
        const outcomeTypeByPrimaryThemeBar = formatMultiSeriesChartData(chartData.outcome_type_by_primary_themes, 'outcomes', ['Local Issue', 'Service Wide', 'Notable Practice'], 'tot');

        // ======> Outcome Type by Secondary Themes
        const outcomeTypeBySecondaryThemeBar = formatMultiSeriesChartData(chartData.outcome_type_by_secondary_themes, 'outcomes', ['Local Issue', 'Service Wide', 'Notable Practice'], 'tot');

        //  ========> Outcome Type
        const outcomeTypeBar = formatSingleSeriesBarChartData(chartData.outcome_type, 'tot');

        // ========>  Count of Debriefs by Learning Identified
        const debriefByLearningBar = formatSingleSeriesBarChartData(chartData.learning, 'tot');

        // Incident L1, L2, and L3
        const incidentL1Bar = formatSingleSeriesBarChartData(chartData.incidentsL1, 'tot');
        const incidentL2Bar = formatSingleSeriesBarChartData(chartData.incidentsL2, 'tot');
        const incidentL3Bar = formatSingleSeriesBarChartData(chartData.incidentsL3, 'tot');

        return {
            author: authStore.getLoggedUserName, //  ========> Setup author and generated date,
            debriefsLIByCallSignBar,
            outcomeTypeByCallSignBar,
            outcomeTypeByPrimaryThemeBar,
            outcomeTypeBySecondaryThemeBar,
            outcomeTypeBar,
            debriefByLearningBar,
            incidentL1Bar,
            incidentL2Bar,
            incidentL3Bar,
        };
    };

    const formatDebriefTableData = (rawData) => {
        return rawData?.map((debrief) => {
            return {
                // ...debrief,
                incident_number: debrief.incident.incident_number,
                incident_category: incident_categories.find((incident_category) => incident_category.id == debrief?.incident?.category)?.name,
                incident_date: formatDate(debrief.incident?.incident_date),
                incident_type_name: flatIncidentTypes.value?.find((item) => item.id == parseInt(debrief.incident.incident_type_id))?.name,
                appliance_name: debrief?.appliance?.name,
                author_name: getFullName(debrief?.author),
                learning_outcomes: debrief.learning_outcome?.map((learning, index) => `LO${index + 1}:` + (learning.details ? removeHtmlTagsFromParagraph(learning.details) : 'No Description found')),
            };
        });
    };

    // ======> Task Reports Section:-
    const getDynamicTaskReport = async () => {
        try {
            loading.value = true;
            let page_size = params.value.rows ?? '';
            let page = params.value.page ? params.value.page + 1 : '';
            const bodyparams = { ...taskCriteria.value, page_size: page_size, page: page, company_id: company_id.value, output: 'json' };
            const response = await authApi.post(`${REPORT_URL}/task/dynamic`, bodyparams);
            if (response.statusCode === 200) {
                dynamicTask.value = response.data;
                totalTaskRecords.value = response.data?.raw.total;
                // after tasks have been loaded, load tasks details
                await loadTasksTableDetails();
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to dynamic task report data');
        } finally {
            loading.value = false;
        }
    };

    const loadTasksTableDetails = async () => {
        try {
            // first fetch all tasks id in the table
            const allTasks = dynamicTask.value?.raw?.data;
            let newTasksArr = [];
            for (let i = 0; i < allTasks.length; i++) {
                let task = allTasks[i];
                // const latestComments = await commentStore.getlatest5CommentsByTask(task.id);
                let tempTask = {
                    ...task,
                    theme_name: task.theme?.name,
                    department_to: task.department_to?.name,
                    task_progress: progress_status.find((status) => status.id == task.progress),
                    task_priority: priority_type.find((priority) => priority.id == task.priority)?.name,
                    comments: task.comments,
                };
                newTasksArr.push(tempTask);
            }
            tasks.value = newTasksArr;
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to load tasks details');
        }
    };

    // API request to generate template response for downloading Task Report
    const generateTaskTemplateResponse = async () => {
        // 1: Format the request payload for the API
        const requestPayload = { ...taskCriteria.value, company_id: company_id.value, output: 'template' };
        return await ReportService.fetchTaskReport(requestPayload);
    };

    const downloadReport = async (templateJson, template) => {
        try {
            const templateId = template === 'task' ? TASK_REPORT_TEMPLATE_ID : DEBRIEF_REPORT_TEMPLATE_ID;
            // 1: Send the payload JSON to APITemplate.io
            const response = await ReportService.downloadTemplateReport(templateId, templateJson);
            // 2: Response will have download_url so
            return response.data.download_url;
        } catch (error) {
            showToast({}, 'Print error: ' + error?.data?.message ?? 'Failed to print Task report');
            throw error;
        }
    };

    //helpers
    const initDebriefCriteria = () => {
        debriefCriteria.value = {
            company_id: null,
            incident_number: null,
            category: null,
            incident_type_id: [],
            incident_id: [],
            learning: 'Both',
            appliance_id: [],
            author_id: [],
            theme_id: [],
            learning_outcome_type: [],
            incident_date_from: null,
            incident_date_to: null,
        };
    };

    const initTaskCriteria = () => {
        taskCriteria.value = {
            company_id: null,
            title: null,
            description: null,
            status: [],
            priority: [],
            start_date_from: null,
            start_date_to: null,
            due_date_from: null,
            due_date_to: null,
            source_id: [],
            incident_id: [],
            assigned_to_id: [],
            from_department_id: [],
            to_department_id: [],
            theme_id: [],
        };
    };

    const initparams = () => {
        params.value.rows = 10;
        params.value.page = 1;
    };

    return {
        progress_status,
        priority_type,
        //state
        dynamicDebrief,
        debriefCriteria,
        taskCriteria,
        params,
        loading,
        dynamicTask,
        totalTaskRecords,
        //getter
        tasks,
        //actions
        // ===>Dynamic Report Data
        getDynamicDebriefReportData,
        printDebriefReport,
        getDynamicTaskReport,
        // ==>Template Downloads
        generateTaskTemplateResponse,
        downloadReport,
        //helper
        initDebriefCriteria,
        initparams,
        initTaskCriteria,
    };
});
